import type { ReactElement } from 'react';
import type { DocumentSection, PlanPath, Proposal } from '@shared/types';
import { tacticFor } from '../../lib/plan';
import { ChevronRightIcon, CloseIcon, MagnifierIcon, MicIcon } from './icons';

/**
 * Slide 09 hero for `paid_search` on v3 only (`docs/momentum-v3-slides.md`
 * product_divider; media-pair-contract.md "Slide 09"). Inline HTML/CSS + a
 * handful of tiny inline SVG glyphs (magnifier/mic/close/chevron from
 * `./icons`) — no screenshot, no kit PNG, so it can never carry another
 * company's real UI or brand. `searchHeroModel` below is the pure data
 * derivation the audit calls for ("agent supplies content inputs, the
 * renderer owns geometry"); this component only lays the model out.
 *
 * Accuracy rule (audit "Important accuracy rules"): no ratings, stars,
 * prices, ranks or counts anywhere in this visual — only "Sponsored" labels
 * and the copy the model carries.
 */

export interface SearchResultModel {
  query: string;
  title: string;
  desc: string;
  /** Up to 2 items: `[landingLabel, conversionLabel]` (media-pair-contract.md's fixed "sitelink 1 / sitelink 2", 3rd omitted). */
  sitelinks: string[];
  thumbUrl?: string | null;
}

export interface SearchHeroModel {
  advertiserName: string;
  /** Hostname only, no protocol/www — blank when the advertiser has no domain on file. */
  domain: string;
  logoUrl: string | null;
  /** Laptop: top search bar + first "Sponsored" block. */
  resultA: SearchResultModel;
  /** Laptop: second "Sponsored" block. Phone: its one search + "Sponsored" block. */
  resultB: SearchResultModel;
}

function hostnameOf(value: string | null | undefined): string {
  if (!value) return '';
  const trimmed = value.trim();
  if (!trimmed) return '';
  const withProtocol = /^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  try {
    return new URL(withProtocol).hostname.replace(/^www\./, '');
  } catch {
    return trimmed.replace(/^www\./, '').replace(/\/.*$/, '');
  }
}

/** First `high_intent` keyword (else the first keyword at all), optionally excluding one already used so the laptop and phone don't always echo the same term. */
export function keywordPick(proposal: Proposal, excluding?: string): string | null {
  const keywords = proposal.mediaPlan.keywords;
  const highIntent = keywords.find((kw) => kw.category === 'high_intent' && kw.keyword !== excluding);
  if (highIntent) return highIntent.keyword;
  const other = keywords.find((kw) => kw.keyword !== excluding);
  if (other) return other.keyword;
  return keywords[0]?.keyword ?? null;
}

/** The sibling `media_<productId>_plan` section's `dual_path` paths, when it has any (media-pair-contract.md "Derived defaults"). */
function planPathSibling(proposal: Proposal, productId: string): PlanPath[] | null {
  const sections = proposal.document?.sections ?? [];
  const sibling = sections.find(
    (candidate) =>
      candidate.productId === productId && (candidate.type === 'product_plan' || candidate.type === 'media_product'),
  );
  return sibling?.content.paths ?? null;
}

function sitelinksFor(path: PlanPath | undefined): string[] {
  if (!path) return [];
  return [path.landingLabel, path.conversionLabel].filter((label) => !!label && label.trim().length > 0);
}

/**
 * Pure derivation (no rendering) for `SearchDeviceVisual`'s two result
 * models. The agent may override any of query/title/desc per side via
 * `content.fields.searchQueryA` / `searchTitleA` / `searchDescA` (…B);
 * absent fields fall back to the sibling plan section's `paths` (dual_path),
 * then to the plan's own keywords / the tactic's strategic role.
 *
 * Every field here can be agent-authored copy (`fields.search*`,
 * `path.label`/`.role`/`.searchThemes`, `path.landingLabel`/
 * `.conversionLabel`, `tactic.role`) and so can carry a live `{{binding}}`
 * token - the caller's `useBindings()` resolver is threaded through and
 * applied to every string before it reaches `SearchDeviceVisual`'s plain-JSX
 * rendering, same rule `StrategyPage` applies to its own text
 * (`StrategyPages.tsx`), just one level removed since this is a pure
 * function, not a component.
 */
export function searchHeroModel(
  proposal: Proposal,
  section: DocumentSection,
  resolve: (text: string) => string,
): SearchHeroModel {
  const productId = section.productId ?? 'paid_search';
  const advertiserName = proposal.advertiser.name?.trim() || 'This advertiser';
  const domain = hostnameOf(proposal.advertiser.domain);
  const logoUrl = proposal.advertiser.logoUrl ?? null;
  const fields = section.content.fields ?? {};
  const paths = planPathSibling(proposal, productId);
  const pathA = paths?.[0];
  const pathB = paths?.[1];
  const tactic = tacticFor(proposal, productId);
  const imageUrl = section.content.imageUrl || null;

  const fallbackQueryA = pathA?.searchThemes?.[0] ?? keywordPick(proposal) ?? '';
  const queryA = resolve(fields.searchQueryA?.trim() || fallbackQueryA);
  const fallbackQueryB = pathB?.searchThemes?.[0] ?? keywordPick(proposal, queryA || undefined) ?? queryA;
  const queryB = resolve(fields.searchQueryB?.trim() || fallbackQueryB);

  const titleA = resolve(fields.searchTitleA?.trim() || (pathA ? `${advertiserName} | ${pathA.label}` : advertiserName));
  const titleB = resolve(fields.searchTitleB?.trim() || (pathB ? `${advertiserName} | ${pathB.label}` : advertiserName));

  const descA = resolve(fields.searchDescA?.trim() || pathA?.role || tactic?.role || '');
  const descB = resolve(fields.searchDescB?.trim() || pathB?.role || tactic?.role || '');

  return {
    advertiserName,
    domain,
    logoUrl,
    resultA: { query: queryA, title: titleA, desc: descA, sitelinks: sitelinksFor(pathA).map(resolve), thumbUrl: imageUrl },
    resultB: { query: queryB, title: titleB, desc: descB, sitelinks: sitelinksFor(pathB).map(resolve), thumbUrl: imageUrl },
  };
}

/* --------------------------------------------------------------------- */
/* Presentation                                                          */
/* --------------------------------------------------------------------- */

function GoogleWordmark({ className }: { className?: string }): ReactElement {
  return (
    <div className={`mv3-searchhero-wordmark${className ? ` ${className}` : ''}`} aria-hidden="true">
      <span className="mv3-g-blue">G</span>
      <span className="mv3-g-red">o</span>
      <span className="mv3-g-yellow">o</span>
      <span className="mv3-g-blue">g</span>
      <span className="mv3-g-green">l</span>
      <span className="mv3-g-red">e</span>
    </div>
  );
}

function SearchBar({ query, compact }: { query: string; compact?: boolean }): ReactElement {
  return (
    <div className={`mv3-searchhero-searchbar${compact ? ' mv3-searchhero-searchbar-compact' : ''}`}>
      <MagnifierIcon className="mv3-searchhero-searchbar-icon" size={compact ? 14 : 16} />
      <span className="mv3-searchhero-query">{query}</span>
      <span className="mv3-searchhero-searchbar-glyphs">
        <CloseIcon size={compact ? 11 : 13} />
        <MicIcon size={compact ? 14 : 16} />
      </span>
    </div>
  );
}

const TABS = ['All', 'Events', 'Images', 'Videos', 'Shopping', 'Maps'];

function TabRow({ compact }: { compact?: boolean }): ReactElement {
  return (
    <div className={`mv3-searchhero-tabs${compact ? ' mv3-searchhero-tabs-compact' : ''}`}>
      {TABS.map((tab, index) => (
        <span key={tab} className={`mv3-searchhero-tab${index === 0 ? ' mv3-searchhero-tab-active' : ''}`}>
          {tab}
        </span>
      ))}
    </div>
  );
}

function Favicon({ advertiserName, logoUrl }: { advertiserName: string; logoUrl: string | null }): ReactElement {
  if (logoUrl) {
    return <img src={logoUrl} alt="" className="mv3-searchhero-favicon mv3-searchhero-favicon-img" />;
  }
  const letter = advertiserName.trim().charAt(0).toUpperCase() || '?';
  return (
    <span className="mv3-searchhero-favicon mv3-searchhero-favicon-glyph" aria-hidden="true">
      {letter}
    </span>
  );
}

function Thumb({ thumbUrl, compact }: { thumbUrl?: string | null; compact?: boolean }): ReactElement {
  return (
    <div className={`mv3-searchhero-thumb${compact ? ' mv3-searchhero-thumb-compact' : ''}`}>
      {thumbUrl ? <img src={thumbUrl} alt="" className="mv3-searchhero-thumb-img" /> : null}
    </div>
  );
}

function SponsoredResult({
  advertiserName,
  domain,
  logoUrl,
  result,
  compact,
}: {
  advertiserName: string;
  domain: string;
  logoUrl: string | null;
  result: SearchResultModel;
  compact?: boolean;
}): ReactElement {
  return (
    <div className={`mv3-searchhero-result${compact ? ' mv3-searchhero-result-compact' : ''}`}>
      <div className="mv3-searchhero-result-main">
        <div className="mv3-searchhero-result-meta">
          <Favicon advertiserName={advertiserName} logoUrl={logoUrl} />
          <div className="mv3-searchhero-result-metatext">
            <span className="mv3-searchhero-sponsored">Sponsored</span>
            <span className="mv3-searchhero-domain">{domain || advertiserName}</span>
          </div>
        </div>
        <div className="mv3-searchhero-result-title">{result.title}</div>
        <div className="mv3-searchhero-result-desc">{result.desc}</div>
        {!compact && result.sitelinks.length > 0 ? (
          <div className="mv3-searchhero-sitelinks">
            {result.sitelinks.map((label) => (
              <span className="mv3-searchhero-sitelink" key={label}>
                {label}
              </span>
            ))}
          </div>
        ) : null}
      </div>
      {!compact ? <Thumb thumbUrl={result.thumbUrl} /> : null}
    </div>
  );
}

export function SearchDeviceVisual({ model, className }: { model: SearchHeroModel; className?: string }): ReactElement {
  return (
    <div className={`mv3-searchhero${className ? ` ${className}` : ''}`}>
      <div className="mv3-searchhero-devices">
        <div className="mv3-searchhero-laptop">
          <div className="mv3-searchhero-laptop-bezel">
            <div className="mv3-searchhero-laptop-page">
              <GoogleWordmark />
              <SearchBar query={model.resultA.query} />
              <TabRow />
              <div className="mv3-searchhero-results">
                <SponsoredResult
                  advertiserName={model.advertiserName}
                  domain={model.domain}
                  logoUrl={model.logoUrl}
                  result={model.resultA}
                />
                <SponsoredResult
                  advertiserName={model.advertiserName}
                  domain={model.domain}
                  logoUrl={model.logoUrl}
                  result={model.resultB}
                />
              </div>
            </div>
          </div>
          <div className="mv3-searchhero-laptop-base" aria-hidden="true" />
        </div>
        <div className="mv3-searchhero-phone">
          <div className="mv3-searchhero-phone-bezel">
            <div className="mv3-searchhero-phone-notch" aria-hidden="true" />
            <div className="mv3-searchhero-phone-page">
              <div className="mv3-searchhero-phone-status" aria-hidden="true" />
              <GoogleWordmark className="mv3-searchhero-wordmark-compact" />
              <SearchBar query={model.resultB.query} compact />
              <TabRow compact />
              <SponsoredResult
                advertiserName={model.advertiserName}
                domain={model.domain}
                logoUrl={model.logoUrl}
                result={model.resultB}
                compact
              />
              {model.resultB.sitelinks.length > 0 ? (
                <div className="mv3-searchhero-phone-list">
                  {model.resultB.sitelinks.map((label) => (
                    <div className="mv3-searchhero-phone-row" key={label}>
                      <span>{label}</span>
                      <ChevronRightIcon size={14} />
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * The SEM plan slide's sponsored-search card (`ProductPlanDualPath.tsx`): one
 * browser-less results page — search bar carrying the plan's top keyword and
 * this advertiser's "Sponsored" result under it. Same markup and accuracy
 * rule as the divider hero above (only "Sponsored" labels, no ratings or
 * counts); `.mv3-semplan-serp` sizes it up for the 1920 canvas.
 */
export function SponsoredSearchCard({ model, query }: { model: SearchHeroModel; query: string }): ReactElement {
  return (
    <div className="mv3-semplan-serp">
      <div className="mv3-semplan-serp-page">
        <GoogleWordmark />
        <SearchBar query={query} />
        <SponsoredResult
          advertiserName={model.advertiserName}
          domain={model.domain}
          logoUrl={model.logoUrl}
          result={model.resultA}
        />
      </div>
    </div>
  );
}
